// Aggregates every material registered on one household into its CO2 and
// monetary effect.
//
// THE RULE THAT MAKES THIS MORE THAN A SUM. The standaard's productlaag says:
//
//   "Meerdere producten wijzen naar EEN effectcode (bv. tochtband/kit/dorpel/
//    kaderprofiel -> ET03). Tel het effect EEN keer per effectcode per woning,
//    niet per product."
//
// So the aggregation groups by effectcode first and applies the kengetal once
// per code, rather than summing per product. For a per-unit code that happens
// to give the same answer, because the kengetal is linear in units — but for a
// per-bewoner or per-woning code it does not: two douchetimers both map to
// ET09, and summing per product would bill the household for twice its
// residents. Grouping is the model the standaard describes, so it is the model
// implemented here rather than a shortcut that only coincidentally matches.
//
// NOT APPLIED: the cluster caps of afbakeningsregels 11 and 12 (ruimteverwarming
// and one-measure-per-lichtpunt). The standaard prescribes a cap but publishes
// no value for it, and inventing one would be our assumption rather than the
// standaard's. The total can therefore overstate a household that received
// several overlapping ruimteverwarming measures; the detail view carries that
// caveat. Revisit when the standaard publishes a number.
//
// NEVER THROWS, and an unknown is never a zero.
// AI-CONTEXT: docs/pitfalls.md#ui-strict-validator-kills-autosave
//
// AI-CONTEXT: See docs/features/materials-co2-circulariteit.md

import computeMaterialImpact from './computeMaterialImpact'
import resolveMaterialIntervention from './resolveMaterialIntervention'
import materialDisplayName from './materialDisplayName'

/**
 * @typedef {object} CodeContribution
 * @property {string} code
 * @property {string} name - Interventie label, for the detail view
 * @property {'perYear'|'perUnit'} basis
 * @property {number} units
 * @property {number} co2Kg
 * @property {number|null} privateEur - null when this interventie has no bill saving
 * @property {number} societalEur
 *
 * @typedef {object} UnknownContribution
 * @property {string} materialID
 * @property {string} title
 * @property {string} status - Why no figure could be produced
 *
 * @typedef {object} HouseholdImpact
 * @property {number|null} co2Kg - null when nothing at all could be calculated
 * @property {number|null} privateEur
 * @property {number|null} societalEur
 * @property {Array<CodeContribution>} perCode
 * @property {Array<UnknownContribution>} unknown
 * @property {number} knownCount - Materials that produced a figure
 * @property {number} unknownCount
 */

/** @type {HouseholdImpact} */
const EMPTY = {
    co2Kg: null,
    privateEur: null,
    societalEur: null,
    perCode: [],
    unknown: [],
    knownCount: 0,
    unknownCount: 0,
}

/**
 * @param {object} input
 * @param {Array<{materialID: string, quantity: *}>} input.rows - From computeHouseholdMaterials
 * @param {Array<object>} input.materials - Master Materials docs
 * @param {{byCode: object}} input.library - From adaptInterventieDocument
 * @param {*} [input.residents] - The household's resident count, when recorded
 * @returns {HouseholdImpact}
 */
const computeHouseholdImpact = (input) => {
    const rows = Array.isArray(input?.rows) ? input.rows : []
    const materials = Array.isArray(input?.materials) ? input.materials : []
    const library = input?.library
    if (!rows.length || !library?.byCode) return EMPTY

    const materialById = new Map(materials.map((m) => [m?.ID, m]))

    /** @type {Map<string, {intervention: object, units: number}>} */
    const unitsByCode = new Map()
    const unknown = []
    let knownCount = 0

    rows.forEach((row) => {
        const doc = materialById.get(row?.materialID) || null
        const resolved = resolveMaterialIntervention(doc, library)
        const impact = computeMaterialImpact(
            { quantity: row?.quantity, resolved },
            { residents: input?.residents }
        )

        if (impact.status !== 'ok') {
            unknown.push({
                materialID: row?.materialID,
                title: materialDisplayName(doc),
                status: impact.status,
            })
            return
        }

        knownCount += 1
        const existing = unitsByCode.get(impact.code)
        if (!existing) {
            unitsByCode.set(impact.code, { intervention: resolved.intervention, units: impact.units })
            return
        }
        // Per-bewoner and per-woning codes are already whole-household figures,
        // so a second product on the same code adds nothing. Only per-unit
        // codes accumulate.
        if (resolved.intervention.scope === 'perUnit') {
            existing.units += impact.units
        }
    })

    if (!unitsByCode.size) {
        return { ...EMPTY, unknown, unknownCount: unknown.length }
    }

    const perCode = [...unitsByCode.entries()].map(([code, { intervention, units }]) => ({
        code,
        name: intervention.name,
        basis: intervention.basis,
        units,
        co2Kg: intervention.co2KgPerUnit * units,
        privateEur: intervention.privateEurPerUnit === null
            ? null
            : intervention.privateEurPerUnit * units,
        societalEur: intervention.societalEurPerUnit * units,
    }))

    return {
        co2Kg: perCode.reduce((sum, c) => sum + c.co2Kg, 0),
        // A null contribution here is a fact, not a gap: a reuse interventie
        // genuinely lowers no energy bill, so it adds zero to this total while
        // still counting towards CO2.
        privateEur: perCode.reduce((sum, c) => sum + (c.privateEur || 0), 0),
        societalEur: perCode.reduce((sum, c) => sum + c.societalEur, 0),
        perCode,
        unknown,
        knownCount,
        unknownCount: unknown.length,
    }
}

export default computeHouseholdImpact
